import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getSupabase } from "../../lib/supabase/client";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Switch } from "../ui/switch";
import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";

type ClientType = "broker" | "investor" | "operator" | "other";

type SignUpModalProps = {
  isOpen: boolean;
  onClose: () => void;
  planId?: string;
  planName?: string;
};

export default function SignUpModal({
  isOpen,
  onClose,
  planId,
  planName,
}: SignUpModalProps) {
  const supabase = getSupabase();
  const nav = useNavigate();

  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [clientType, setClientType] = useState<ClientType | "">("");
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [ackRisk, setAckRisk] = useState(false);

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const cleanEmail = email.trim().toLowerCase();
  const validEmail = /\S+@\S+\.\S+/.test(cleanEmail);
  const pwOk = pw1.length >= 8 && pw1 === pw2;
  const canSubmit =
    fullName.trim().length > 1 &&
    validEmail &&
    !!clientType &&
    pwOk &&
    agreeTerms &&
    ackRisk &&
    !busy;

  useEffect(() => {
    if (!isOpen) return;
    setFullName("");
    setEmail("");
    setClientType("");
    setPw1("");
    setPw2("");
    setAgreeTerms(false);
    setAckRisk(false);
    setErr(null);
    setMsg(null);
  }, [isOpen]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!canSubmit) return;

    setErr(null);
    setMsg(null);
    setBusy(true);

    try {
      const { data, error } = await supabase.auth.signUp({
        email: cleanEmail,
        password: pw1,
        options: {
          data: {
            full_name: fullName.trim(),
            client_type: clientType,
          },
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      });

      if (error) throw error;

      if (data.user && !data.session) {
        setMsg("Account created! Confirm your email.");
      } else {
        setMsg("Success! Redirecting...");
        setTimeout(() => {
          onClose();
          nav("/dashboard");
        }, 1500);
      }
    } catch (e: any) {
      setErr(e?.message ?? "Sign up failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg md:max-w-3xl flex flex-col max-h-[90vh] p-0">
        <div className="px-6 pt-1 pb-2 border-b">
          <DialogHeader>
            <DialogTitle>Join Hotel Foundry</DialogTitle>
            <DialogDescription>
              Get started with the{" "}
              <span className="font-semibold text-blue-700">
                {planName || "Beta-tester"}
              </span>{" "}
              plan.
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="overflow-y-auto custom-scroll-thin flex-1 px-6 pb-4 pt-4">
          <form id="signup_form" onSubmit={onSubmit} className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Full Name *
                </label>
                <Input
                  placeholder="Your full name"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  required
                  className="h-10"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Email Address *
                </label>
                <Input
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="h-10"
                />
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">
                Type of Client *
              </label>
              <Select
                value={clientType}
                onValueChange={(v: any) => setClientType(v)}
              >
                <SelectTrigger className="h-10">
                  <SelectValue placeholder="Select your client type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="broker">Broker</SelectItem>
                  <SelectItem value="investor">Investor</SelectItem>
                  <SelectItem value="operator">Operator</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Password *
                </label>
                <Input
                  type="password"
                  placeholder="Minimum 8 characters"
                  value={pw1}
                  onChange={(e) => setPw1(e.target.value)}
                  required
                  className="h-10"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Confirm Password *
                </label>
                <Input
                  type="password"
                  placeholder="Repeat password"
                  value={pw2}
                  onChange={(e) => setPw2(e.target.value)}
                  required
                  className="h-10"
                />
              </div>
            </div>
            <div className="space-y-4 rounded-lg border border-slate-200 p-4 bg-slate-50">
              <label className="flex items-start gap-3">
                <Switch
                  checked={agreeTerms}
                  onCheckedChange={(v) => setAgreeTerms(!!v)}
                  className="mt-1 data-[state=checked]:bg-blue-600"
                />
                <span className="text-sm text-slate-700">
                  I agree to the{" "}
                  <a
                    className="underline text-blue-600 hover:text-blue-700"
                    href="/legal/terms"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Terms &amp; Conditions
                  </a>{" "}
                  and{" "}
                  <a
                    className="underline text-blue-600 hover:text-blue-700"
                    href="/legal/privacy"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Privacy Policy
                  </a>
                  .
                </span>
              </label>
              <label className="flex items-start gap-3">
                <Switch
                  checked={ackRisk}
                  onCheckedChange={(v) => setAckRisk(!!v)}
                  className="mt-1 data-[state=checked]:bg-blue-600"
                />
                <span className="text-sm text-slate-700">
                  I acknowledge the platform is indicative, may contain errors,
                  and should not be solely relied upon for investment decisions.
                </span>
              </label>
            </div>
            {err && <p className="text-sm text-red-600">{err}</p>}
            {!pwOk && (pw1 || pw2) && (
              <p className="text-xs text-amber-700">
                Passwords must match and be at least 8 characters.
              </p>
            )}
            {msg && <p className="text-sm text-emerald-700">{msg}</p>}
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-xs leading-relaxed text-amber-900 shadow-inner">
              {" "}
              <strong>Important Disclaimer.</strong> Hotel Foundry provides
              analysis tools for educational purposes. Projections and outputs
              are estimates and may not reflect actual performance. Always
              perform independent due diligence and consult qualified advisors.
              You are solely responsible for your decisions.
            </div>
          </form>
        </div>

        <div className="border-t px-6 py-4 bg-white">
          <Button
            type="submit"
            form="signup_form"
            className="w-full h-10 text-base bg-primary-blue"
            disabled={!canSubmit}
          >
            {busy ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Creating Account…
              </span>
            ) : (
              "Create Account"
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
